import type { Dayjs } from 'dayjs';
import type { Tagged } from 'type-fest';
import { dayjs } from '../../../shared/utils/date';
import type { Account } from '../account';
import { MutableEntity } from '../common';
import { UniqueId } from '../UniqueId';
import { Organization } from './index';

const idGenerator = UniqueId.GetFactory(10);

export type VerifiedDomain = MutableEntity & {
  id: VerifiedDomain.Id;
  organizationId: Organization.Id;
  domain: string;
  status: VerifiedDomain.Status;
  verificationToken: string;
  verifiedAt: Dayjs | undefined;
};

export namespace VerifiedDomain {
  export type Id = Tagged<UniqueId, 'verifiedDomainId'>;

  export namespace Id {
    export const Generate = () => idGenerator.generate() as Id;
    export const FromString = (id: string): Id => idGenerator.FromString(id) as Id;
  }

  export const Status = {
    Pending: 'pending',
    Verified: 'verified',
  } as const;
  export type Status = (typeof Status)[keyof typeof Status];

  export type Props = MutableEntity.Props & {
    id: string;
    organizationId: string;
    domain: string;
    status: Status;
    verificationToken: string;
    verifiedAt?: string | Date | Dayjs;
  };

  export const Construct = (props: Props): VerifiedDomain => {
    return {
      ...MutableEntity.Construct(props),
      id: Id.FromString(props.id),
      organizationId: Organization.Id.FromString(props.organizationId),
      domain: props.domain,
      status: props.status,
      verificationToken: props.verificationToken,
      verifiedAt: props.verifiedAt ? dayjs(props.verifiedAt) : undefined,
    };
  };

  const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

  /** メールアドレスや入力文字列から検証用ドメイン名を正規化する */
  export const NormalizeDomain = (value: string): string => {
    const trimmed = value.trim().toLowerCase();
    const atIndex = trimmed.lastIndexOf('@');
    return atIndex >= 0 ? trimmed.slice(atIndex + 1) : trimmed;
  };

  export const IsValidDomainFormat = (domain: string): boolean => DOMAIN_PATTERN.test(domain);

  export const ExtractEmailDomain = (email: string): string | null => {
    const normalized = NormalizeDomain(email);
    return IsValidDomainFormat(normalized) ? normalized : null;
  };

  export const IsVerified = (verifiedDomain: VerifiedDomain): boolean => verifiedDomain.status === Status.Verified;

  export const BuildVerificationDnsHost = (domain: string): string => `_auth-verify.${domain}`;

  export const BuildExpectedTxtValue = (verificationToken: string): string => `auth-verify=${verificationToken}`;

  export const MatchesVerificationTxtRecord = (verificationToken: string, txtRecords: string[][]): boolean => {
    const expected = BuildExpectedTxtValue(verificationToken);
    return txtRecords.some((chunks) => chunks.join('') === expected);
  };

  const generateVerificationToken = (): string => {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  export namespace Create {
    export type Props = {
      domain: string;
    };
    export type Params = {
      operatorId: Account.Id;
      organizationId: Organization.Id;
      props: Props;
    };
  }
  export const Create = (params: Create.Params): VerifiedDomain => {
    const { operatorId, organizationId, props } = params;
    const domain = NormalizeDomain(props.domain);
    if (!IsValidDomainFormat(domain)) {
      throw new Error(`Invalid domain: ${props.domain}`);
    }

    return {
      ...MutableEntity.Create({ operatorId }),
      id: Id.Generate(),
      organizationId,
      domain,
      status: Status.Pending,
      verificationToken: generateVerificationToken(),
      verifiedAt: undefined,
    };
  };

  export namespace MarkVerified {
    export type Params = {
      operatorId: Account.Id;
      cur: VerifiedDomain;
    };
  }
  export const MarkVerified = ({ operatorId, cur }: MarkVerified.Params): VerifiedDomain => {
    return {
      ...cur,
      ...MutableEntity.Update({ cur, operatorId }),
      status: Status.Verified,
      verifiedAt: dayjs(),
    };
  };
}
